import { useEffect, useState } from "react";
import {
  apiOrigin,
  buildIdentity,
  deploymentEnvironmentFromWebappHost,
  type BuildIdentity,
  UNKNOWN_BUILD_VALUE,
} from "@a7y/types/deployment-environment";

/**
 * Shortens a full SHA to its conventional 7-char display form. Returns the
 * `unknown` sentinel unchanged so callers never render a truncated "unknow".
 */
function shortSha(sha: string): string {
  return sha === UNKNOWN_BUILD_VALUE ? sha : sha.slice(0, 7);
}

/**
 * Displays a fixed banner at the top of the page for non-production environments.
 * Yellow for staging, blue for dev, purple for Sandbox. Mount at the root
 * layout of each frontend app.
 *
 * SSR-safety: the banner returns null on the first synchronous render (server
 * and client both). Once mounted on the client, a useEffect flips the flag and
 * the banner appears post-hydration. This avoids the hydration mismatch where
 * the server would render null but the client would render the banner.
 *
 * A7Y SBX-03: on the Sandbox host ONLY, the banner additionally fetches the
 * frontend's own `/version.json` (the shared `BuildIdentity` the Vite build
 * emits — see `a7y-webapp/vite.config.ts`) to show "branch @ sha7", and the
 * API's `/health` to compare `build.sha` — a mixed deployment (frontend and
 * API built from different commits) is exactly the failure mode a single
 * persistent, branch-switching environment can produce, and it must be
 * visible rather than silently mismatched. Both fetches are best-effort: a
 * failed or missing response leaves the banner showing the plain label,
 * never a crash. Staging and dev banner behavior is unchanged — neither
 * fetches anything.
 *
 * On dev and staging the banner carries an X that hides it until the next
 * full page load (TPF, 2026-10-26: to judge the layout under it). Sandbox
 * keeps it, since its build-mismatch warning must stay visible.
 *
 * `placement="sidebar-foot"` draws the dev and staging banner as a small
 * badge at the foot of the app's sidebar — only as wide as its label plus a
 * little padding — instead of a strip across the top of the page (TPF,
 * 2026-10-26, A7Y-5791). Sandbox stays a full-width strip: its branch, sha
 * and API-mismatch text need the room.
 */
export function StagingBanner({ placement = "top" }: { placement?: "top" | "sidebar-foot" } = {}) {
  const [mounted, setMounted] = useState(false);
  const [frontendIdentity, setFrontendIdentity] = useState<BuildIdentity | null>(null);
  const [apiBuildSha, setApiBuildSha] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => setMounted(true), []);

  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const isSandbox = deploymentEnvironmentFromWebappHost(hostname) === "sandbox";
  const isStaging = hostname.includes("-staging.");
  const isDev = hostname === "localhost" || hostname === "127.0.0.1";

  // Sandbox-only: resolve the frontend's own build identity and the API's
  // reported build, so a mixed deployment can never hide behind a banner that
  // only ever says "SANDBOX ENVIRONMENT".
  useEffect(() => {
    if (!mounted || !isSandbox) return;
    let cancelled = false;

    fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { deploymentEnvironment?: unknown; sha?: string; branch?: string; builtAt?: string } | null) => {
        if (cancelled || !data) return;
        setFrontendIdentity(
          buildIdentity({
            deploymentEnvironment: data.deploymentEnvironment,
            sha: data.sha,
            branch: data.branch,
            builtAt: data.builtAt,
          })
        );
      })
      .catch(() => {
        // Non-fatal — the banner still shows the label without branch/sha.
      });

    fetch(`${apiOrigin("sandbox")}/health`, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { build?: { sha?: string } } | null) => {
        if (cancelled) return;
        const sha = data?.build?.sha;
        if (typeof sha === "string" && sha.length > 0) setApiBuildSha(sha);
      })
      .catch(() => {
        // Non-fatal — a failed /health probe must never break the banner.
      });

    return () => {
      cancelled = true;
    };
  }, [mounted, isSandbox]);

  if (!mounted) return null;
  if (!isStaging && !isDev && !isSandbox) return null;
  if (dismissed) return null;

  const asBadge = placement === "sidebar-foot" && !isSandbox;
  const label = isSandbox ? "SANDBOX ENVIRONMENT" : isStaging ? "STAGING ENVIRONMENT" : "DEV ENVIRONMENT";
  const background = isSandbox ? "#7c3aed" : isStaging ? "#f59e0b" : "#3b82f6";
  const color = isSandbox ? "#fff" : isStaging ? "#000" : "#fff";

  const hasBranchSha =
    isSandbox &&
    frontendIdentity !== null &&
    frontendIdentity.branch !== UNKNOWN_BUILD_VALUE &&
    frontendIdentity.sha !== UNKNOWN_BUILD_VALUE;
  const branchShaText = hasBranchSha
    ? ` — ${frontendIdentity!.branch} @ ${shortSha(frontendIdentity!.sha)}`
    : "";

  // Only flag a mismatch once BOTH sides are known — an absent API sha means
  // the probe failed or the deployed API predates the build-info field, never
  // "the API disagrees".
  const apiMismatchSha =
    isSandbox && frontendIdentity !== null && apiBuildSha !== null && apiBuildSha !== frontendIdentity.sha
      ? apiBuildSha
      : null;

  return (
    <div
      /**
       * Addressable so a screenshot harness can suppress it. The banner is
       * correct behaviour — it fires on `localhost`/`127.0.0.1`, which is
       * exactly where a render harness runs — but a capture handed to a
       * reviewer must show the product, not the environment it was captured
       * in. Without this there is nothing to target: no class, and matching on
       * a substring of an inline style object is not a selector anyone should
       * depend on.
       */
      data-testid="staging-banner"
      data-placement={asBadge ? "sidebar-foot" : "top"}
      style={{
        position: "fixed",
        zIndex: 9999,
        background,
        color,
        fontSize: "10px",
        fontWeight: 600,
        lineHeight: "14px",
        ...(asBadge
          ? {
              // Bottom-left, over the sidebar's foot, centred on the bottom
              // bar's 24px row; the dismiss X sits inline after the label.
              left: 16,
              bottom: 4,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "1px 6px 1px 8px",
              borderRadius: 4,
            }
          : { top: 0, left: 0, right: 0, textAlign: "center", padding: "1px 0" }),
      }}
    >
      {label}
      {branchShaText}
      {apiMismatchSha !== null && (
        <span
          data-testid="staging-banner-api-mismatch"
          style={{ color: "#fde047", fontWeight: 800 }}
        >
          {` · API ${shortSha(apiMismatchSha)}`}
        </span>
      )}
      {isSandbox ? null : (
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Hide the environment banner until the page reloads"
          title="Hide until the page reloads"
          data-testid="staging-banner-dismiss"
          style={{
            ...(asBadge ? { position: "static" } : { position: "absolute", top: 0, right: 6 }),
            height: 16,
            width: 16,
            padding: 0,
            border: 0,
            background: "transparent",
            color: "inherit",
            cursor: "pointer",
            fontSize: "12px",
            lineHeight: "16px",
            opacity: 0.85,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}
